import absoluteUrl from 'next-absolute-url';
import { differenceInCalendarYears } from 'date-fns';
import { fetcher } from 'Utils/fetcher';
import { GetServerSideProps } from 'next';
import styled from 'styled-components';
import { useState } from 'react';

import { IEducation, IJob } from '@Types';

import { Container } from 'Atoms/Container';
import { Education } from 'Atoms/Education';
import { Position } from 'Atoms/Position';
import { SeoHead } from 'Atoms/SeoHead';

interface IProps {
	jobs: IJob[];
	education: IEducation[];
}

function About({ jobs, education }: IProps) {
	const [loadedJobs, setLoadedJobs] = useState(2);

	const loadMore = () => {
		setLoadedJobs((prev) => prev + 3);
	};

	return (
		<>
			<SeoHead
				title="About Junaid Umer - Sr. iOS Developer"
				description="I develop iOS Applications in Swift language. I am a strong engineering professional with a Master's focus in Information Technology from the University of Sargodha. I have 4+ years of experience in iOS development working with many frameworks like Uikit, UlFoundation. Mobile Core Services, WebKit. AV Foundation, Core Data, Firebase, REST APIs, User Authentication using Facebook, Twitter. Gmail, Google-Plus, and integrated payment methods using PayPal and Stripe. In my professional field, I have worked on about 10 iOS apps."
			/>

			<Container>
				<Headline>Hey, I&apos;m Junaid Umer</Headline>
				<h3>A Senior iOS Developer from Pakistan</h3>
				<p>
					As a passionate iOS developer, I create amazing applications to make
					the internet a better place. I am an advocate for app performance and
					accessibility.
				</p>
				<p>
					I am 27 years old and have been an iOS developer for as long as I can
					think. The technologies I work with are Swift and XCode IDE with a
					focus on the frameworks UIKit, UIFoundation and Autolayout. I use code
					not only to do my day-to-day job, but also to solve everyday problems
					I come across.
				</p>
				<p>
					When I am not writing code, I love to spend time with my family and
					friends at home in Pakistan or travelling around the Country. I myself
					speak three languages (some better than others). Furthermore, I enjoy
					cooking fresh food when I come home after a long day at the office.
				</p>

				<h2>Experience</h2>
				{jobs.slice(0, loadedJobs).map((job, i) => (
					<Position job={job} key={job.company + i} />
				))}
				<Center>
					{loadedJobs < jobs.length && (
						<button onClick={loadMore}>Load more</button>
					)}
				</Center>

				<h2>Education</h2>
				{education.map((edu) => (
					<Education education={edu} key={edu.course} />
				))}

				<Center>
					<a href="/Junaid-Umer-iOS-Developer.pdf" target="_blank">
						Download a copy of my CV
					</a>
				</Center>
			</Container>
		</>
	);
}

const Headline = styled.h2`
	font-size: 2rem;
	margin-bottom: 0;

	@media screen and (min-width: 768px) {
		font-size: 3rem;

		span {
			display: inline-block;
		}

		@keyframes wave {
			0% {
				transform: rotate(0);
			}
			50% {
				transform: rotate(-10deg);
			}
			100% {
				transform: rotate(10deg);
			}
		}

		&:hover span {
			animation: wave 0.5s ease infinite;
		}
	}
`;

const Center = styled.div`
	text-align: center;
	font-weight: bold;
	margin-top: 2rem;

	button {
		display: block;
		margin: 2rem auto;
		border: 2px solid ${({ theme }) => theme.text};
		color: ${({ theme }) => theme.text};
		padding: 0.5rem 3rem;
		border-radius: 5px;
		background: transparent;
	}
`;

export const getServerSideProps: GetServerSideProps = async ({ req }) => {
	const { origin } = absoluteUrl(req);
	const { positions } = await fetcher(`${origin}/api/jobs`);
	const { education } = await fetcher(`${origin}/api/education`);
	const skills = await fetcher(`${origin}/api/skills`);

	return {
		props: {
			jobs: positions,
			education,
			skills,
		},
	};
};

export default About;
